// src/pages/Ads/Reserve/AdsReserve.jsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "../../../styles/scroll.css";
import { HeaderIconL } from "../../../assets/components/HeaderGroup";
import { useFixedLayout } from "../../../hooks/useFixedLayout";
import { SingleButton, TwinColumn } from "../../../assets/components/ButtonGroup";
import swap from "../../../assets/icons/Arrow/swap.svg";
import ReserveCard from "../../../assets/components/ReserveGroup";
import Popup from "../../../assets/components/PopupGroup";
import { useToast } from "../../../hooks/useToast";
import error from "../../../assets/icons/warning/error_gray.svg";

const TAB_CONFIG = [
    { key: "running", label: "예약 실행 중", title: "전체" },
    { key: "waiting", label: "예약 대기 중", title: "전체" },
    { key: "expired", label: "예약 만료", title: "전체" },
];

// 탭별 빈 상태 문구
const EMPTY_TEXT = {
    running: "예약 실행 중인 목록이 없어요",
    waiting: "예약 대기 중인 목록이 없어요",
    expired: "예약 만료된 목록이 없어요",
};

// KST 기준으로 Date 만들기
const toKstDate = (value) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getTime() + 9 * 60 * 60 * 1000);
};

// ISO / DATETIME → YYYY.MM.DD
const formatDateOnlyDot = (isoString) => {
    const d = toKstDate(isoString) || new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}.${mm}.${dd}`;
};

// YYYY-MM-DD / ISO → YYYY.MM.DD
const formatYmdToDot = (dateStr) => {
    if (!dateStr) return "";
    if (dateStr.includes(".")) return dateStr; // 이미 포맷돼 있으면 그대로

    const d = new Date(dateStr.includes("T") ? dateStr : `${dateStr}T00:00:00`);
    if (Number.isNaN(d.getTime())) return dateStr;

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}.${mm}.${dd}`;
};

// 시작/종료일을 한 줄 텍스트로
const formatPeriodLabel = (start, end) => {
    const startLabel = formatYmdToDot(start);
    const endLabel = formatYmdToDot(end);
    if (startLabel === endLabel) return startLabel;
    return `${startLabel} ~ ${endLabel}`;
};

// 탭 + 정렬방향에 따른 라벨 (둘 다 표시하되, 하나만 강조)
const getSortLabels = (tab, sortOrder) => {
    // 공통 기본값
    let first = "";
    let second = "";
    let activeIsFirst = true;

    if (tab === "running") {
        // 예약 실행 중 : 최근 실행일 | 이전 실행일
        first = "최근 실행일";
        second = "이전 실행일";
        activeIsFirst = sortOrder === "desc"; // desc = 최근 실행일
    } else if (tab === "waiting") {
        // 예약 대기 중 : 빠른 예정일 | 늦은 예정일
        first = "빠른 예정일";
        second = "늦은 예정일";
        activeIsFirst = sortOrder === "asc"; // asc = 빠른 예정일
    } else if (tab === "expired") {
        // 예약 만료 : 최근 만료일 | 이전 만료일
        first = "최근 만료일";
        second = "이전 만료일";
        activeIsFirst = sortOrder === "desc"; // desc = 최근 만료일
    } else {
        first = "최근";
        second = "이전";
        activeIsFirst = sortOrder === "desc";
    }

    return { first, second, activeIsFirst };
};


const AdsReserve = () => {
    const [reserveList, setReserveList] = useState([]);

    // 삭제 팝업 상태
    const [openDelete, setOpenDelete] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const userId = localStorage.getItem("user_id");

    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();

    // 상단 고정
    const { headerRef, headerSpace } = useFixedLayout();

    // 탭 / 정렬 상태
    const [activeTab, setActiveTab] = useState(() => {  // running | waiting | expired
        const initialTab = location.state?.initialTab;
        if (initialTab === "running" || initialTab === "waiting" || initialTab === "expired") {
            return initialTab;
        }
        return "running"; // 기본값
    });
    const [sortOrder, setSortOrder] = useState("desc"); // desc=시작일 최근순, asc=시작일 오래된순

    // 현재 탭에 해당하는 제목
    const activeTabConfig = TAB_CONFIG.find((t) => t.key === activeTab);
    const activeTitle = activeTabConfig?.title || "";



    // 생성/수정 완료 후 리다이렉트되었을 때 토스트 처리
    useEffect(() => {
        if (location.state && location.state.toast) {
            showToast(location.state.toast, {
                placement: "bottom",
                bottomOffset: "68px",
            });

            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location, navigate, showToast]);

    // 탭 바뀔 때 기본 정렬 방향 세팅
    useEffect(() => {
        // 요구사항:
        // - 실행 중, 만료 : 시작일 최근순
        // - 대기 중 : 시작일 빠른순
        if (activeTab === "waiting") {
            setSortOrder("asc");
        } else {
            setSortOrder("desc");
        }
    }, [activeTab]);


    const onCreate = () => {
        navigate("/ads/reserve/create");
    };

    const handleEdit = (item) => {
        navigate("/ads/reserve/update", {
            state: { initialData: item },
        });
    };

    //삭제 (be 통신)
    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;

        const reserveId = deleteTarget.reserve_id;
        const payload = { reserve_id: reserveId };

        try {
            await axios.post(
                `${process.env.REACT_APP_FASTAPI_ADS_URL}/reserve/delete`,
                payload,
                { headers: { "Content-Type": "application/json" } }
            );

            // 로컬 리스트에서 삭제
            setReserveList((prev) =>
                prev.filter((item) => item.reserve_id !== reserveId)
            );

            // 삭제 성공 토스트
            showToast("예약이 삭제되었습니다", {
                placement: "bottom",
                bottomOffset: "68px",
            });
        } catch (err) {
            console.error("예약 삭제 실패:", err);
            alert("예약 삭제에 실패했습니다.");
        } finally {
            handleCloseDeletePopup();
        }
    };

    // 삭제 팝업 열기
    const handleOpenDeletePopup = (item) => {
        setDeleteTarget(item);
        setOpenDelete(true);
    };

    // 삭제 팝업 닫기
    const handleCloseDeletePopup = () => {
        setOpenDelete(false);
        setDeleteTarget(null);
    };

    const isFutureStartDate = (dateStr) => {
        if (!dateStr) return false;
        const d = new Date(
            dateStr.includes("T") ? dateStr : `${dateStr}T00:00:00`
        );
        const today = new Date();
        const startOfToday = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
        );
        return d.getTime() > startOfToday.getTime();
    };

    const isExpiredEndDate = (dateStr) => {
        if (!dateStr) return false;
        const d = new Date(
            dateStr.includes("T") ? dateStr : `${dateStr}T00:00:00`
        );
        const today = new Date();
        const startOfToday = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
        );
        return d.getTime() < startOfToday.getTime();
    };

    const classifyReserve = (item) => {
        const futureStart = isFutureStartDate(item.start_date);
        const expired = isExpiredEndDate(item.end_date);

        if (expired) return "expired"; // end_date 지난 것 → 예약 만료
        if (futureStart) return "waiting"; // start_date 아직 안 온 것 → 예약 대기 중
        return "running"; // 그 외 → 예약 실행 중
    };

    // 목록 가져오기
    useEffect(() => {
        const sendGenerateRequest = async () => {
            const recentData = {
                user_id: Number(userId),
            };
            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_FASTAPI_ADS_URL}/reserve/get/user/list`,
                    recentData,
                    {
                        headers: {
                            "Content-Type": "application/json",
                        },
                    }
                );
                const rawList = response.data;

                const parsedList = rawList.map((item) => ({
                    ...item,
                    upload_times:
                        typeof item.upload_times === "string"
                            ? JSON.parse(item.upload_times)
                            : item.upload_times,
                    weekly_days:
                        typeof item.weekly_days === "string"
                            ? JSON.parse(item.weekly_days)
                            : item.weekly_days,
                    monthly_days:
                        typeof item.monthly_days === "string"
                            ? JSON.parse(item.monthly_days)
                            : item.monthly_days,
                }));

                setReserveList(parsedList);
            } catch (error) {
                console.error("예약 리스트 조회 중 오류:", error);
            }
        };

        sendGenerateRequest();
    }, [userId]);

    const toggleSortOrder = () => {
        setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    };

    // 탭 + 정렬 적용한 리스트 (start_date 기준)
    const filteredList = (() => {
        const copied = [...reserveList];
        const byTab = copied.filter(
            (item) => classifyReserve(item) === activeTab
        );

        byTab.sort((a, b) => {
            const da = new Date(
                a.start_date.includes("T") ? a.start_date : `${a.start_date}T00:00:00`
            ).getTime();
            const db = new Date(
                b.start_date.includes("T") ? b.start_date : `${b.start_date}T00:00:00`
            ).getTime();
            return sortOrder === "desc" ? db - da : da - db;
        });

        return byTab;
    })();

    // 반복 기간(start_date + end_date) 기준 그룹핑
    const groupedByPeriod = filteredList.reduce((acc, item) => {
        const key = `${item.start_date}__${item.end_date}`;
        if (!acc[key]) {
            acc[key] = {
                periodLabel: formatPeriodLabel(item.start_date, item.end_date),
                items: [],
            };
        }
        acc[key].items.push(item);
        return acc;
    }, {});

    const groupedEntries = Object.entries(groupedByPeriod);
    const hasData = groupedEntries.length > 0;

    return (
        <div>
            <div
                className="fixed top-0 left-0 right-0 z-20"
                ref={headerRef}
            >
                <HeaderIconL title="자동업로드" />
            </div>

            <div
                className="w-full scrollbar-none"
                style={{ paddingTop: headerSpace, paddingBottom:"68px" }}
            >
                {/* 상단 안내 */}
                <div className="p-[16px] text-text-default">
                    <h2 className="text-t2 pb-[8px]">업로드를 잊지 않도록 도와드려요</h2>
                    <div className="text-b1 text-text-subtler">
                        <p>지정한 일정에 업로드를 안내하는 푸시 알림을 보내드립니다.</p>
                    </div>
                </div>

                <SingleButton onClick={onCreate}>예약 알림 만들기</SingleButton>

                <div className="py-[16px]">
                    <div className="bg-surface-light-subtle h-[16px]" />
                </div>

                {/* 예약 알림 목록 타이틀 */}
                <h2 className="text-t2 p-[16px]">예약 알림 목록</h2>

                {/* 탭 영역 */}
                <div
                    style={{ top: headerSpace }}
                    className="sticky top-0 z-10 bg-transparent"
                >
                    <div className="bg-surface-light-white mr-[8px]">
                        <div className="pt-[8px] flex w-full">
                            {TAB_CONFIG.map((t) => {
                                const selected = activeTab === t.key;
                                return (
                                    <button
                                        key={t.key}
                                        type="button"
                                        onClick={() => setActiveTab(t.key)}
                                        className={`pb-[8px] flex-1 ${selected
                                            ? "text-t4 text-text-primary border-border-primary border-b-2"
                                            : "text-b1 text-text-subtler border-border-subtle border-b"
                                            }`}
                                    >
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>


                {/* 리스트 & 정렬 컨트롤 */}
                {!hasData ||
                    <>
                        <div className="pt-[16px] px-[16px] flex justify-between">
                            {/* 왼쪽: 탭별 제목 */}
                            <div className="text-t3 text-text-subtle">
                                {activeTitle} {filteredList.length}
                            </div>

                            {/* 정렬 토글 (탭별 라벨) */}
                            <div className="flex items-center justify-end">
                                {(() => {
                                    const { first, second, activeIsFirst } = getSortLabels(activeTab, sortOrder);
                                    const activeLabel = activeIsFirst ? first : second;

                                    return (
                                        <button
                                            type="button"
                                            onClick={toggleSortOrder}
                                            className={"flex items-center"}
                                        >
                                            <span
                                                className={"text-b1 text-text-subtle flex"}
                                            >
                                                {activeLabel}
                                            </span>
                                            <img src={swap} alt="" className="w-[24px]" />

                                        </button>
                                    );
                                })()
                                }
                            </div>
                        </div>

                        {/* 구분선 */}
                        <div className="py-[16px]">
                            <div className="bg-bg-white-subtle h-[12px]" />
                        </div>
                    </>
                }


                {/* 탭/정렬 적용된 목록 */}
                <div className="py-[8px] px-[16px] pb-[32px] space-y-[24px]">
                    {!hasData ? (
                        // 데이터 없을 때: 가운데 정렬
                        <div className="flex flex-col items-center justify-center py-[24px] gap-[16px]">
                            <img src={error} alt="데이터 없음" className="h-[48px]" />
                            <p className="text-b2 text-text-subtler">
                                {EMPTY_TEXT[activeTab] || "목록이 없어요"}
                            </p>
                        </div>
                    ) : (
                        groupedEntries.map(([periodKey, group]) => (
                            <div key={periodKey} className="space-y-[12px]">
                                {/* 기간 헤더 (예: 2025.11.24 또는 2025.11.24 ~ 2025.11.27) */}
                                <p className="text-t4 text-text-subtle pb-[4px]">
                                    {group.periodLabel}
                                </p>

                                {group.items.map((item) => (
                                    <ReserveCard
                                        key={item.reserve_id}
                                        title={item.title}
                                        startDate={formatYmdToDot(item.start_date)}
                                        endDate={formatYmdToDot(item.end_date)}
                                        weeklyDays={item.weekly_days || []}
                                        monthlyDays={item.monthly_days || []}
                                        uploadTimes={item.upload_times || []}
                                        straight={item.straight}
                                        updatedAt={formatDateOnlyDot(
                                            item.updated_at || item.created_at
                                        )}
                                        onDelete={() => handleOpenDeletePopup(item)}
                                        onEdit={() => handleEdit(item)}
                                    />
                                ))}
                            </div>
                        ))
                    )}
                </div>
            </div>

            <Popup
                isOpen={openDelete}
                onClose={handleCloseDeletePopup}
                title="예약 알림을 삭제하시겠습니까?"
                subtitle={
                    deleteTarget ? `${deleteTarget.title} 알림을 삭제합니다.` : ""
                }
                renderButtons={
                    <TwinColumn
                        topType="alert"
                        topText="네, 삭제할래요"
                        bottomType="empty"
                        bottomText="아니요"
                        onTop={handleConfirmDelete}
                        onBottom={handleCloseDeletePopup}
                    />
                }
            />
        </div>
    );
};

export default AdsReserve;
